import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { UseFormGetValues, UseFormReset } from "react-hook-form";

import { useAppSelector } from "@/redux/hooks";
import {
	useGetSearchDefaultsQuery,
	useSaveLastPersonSearchMutation,
} from "@/pages/Private/redux/searchDefaults/searchDefaults.api";
import { PersonLastSearch } from "@/pages/Private/redux/searchDefaults/searchDefaults.schema";
import { searchDefaultsSelector } from "@/pages/Private/redux/searchDefaults/searchDefaults.slice";

import { CreateCompanyEnrich } from "../schema/companyEnrich";
import { applySearchToFormValues, lastSearchFromFormValues } from "../utils/searchDefaultsForm";

type FormValues = Partial<CreateCompanyEnrich>;

/**
 * Where the remembered fields of the form came from: still `waiting` for the defaults, the
 * user's `lastSearch`, the `companyPreset`, or `idle` — nothing was applied.
 */
export type SearchDefaultsStatus = "idle" | "waiting" | "lastSearch" | "companyPreset";

interface UseSearchDefaultsPrefillOptions {
	getValues: UseFormGetValues<CreateCompanyEnrich>;
	reset: UseFormReset<CreateCompanyEnrich>;
	/** The form's code → name map; the defaults' country names are added to it. */
	countryNameMap: MutableRefObject<Map<string, string>>;
	/** Called with the values a prefill or a reset wrote. */
	onApplied: (values: FormValues) => void;
}

/** The remembered fields only — what decides whether the user has started a search of their own. */
const rememberedFields = (values: FormValues): string =>
	JSON.stringify(lastSearchFromFormValues(values));

/**
 * Search defaults for a new source (PREC-3666): it opens with the user's last search, or with the
 * company preset before their first one; "Reset to company preset" applies the preset on demand,
 * and every search the user runs is remembered.
 *
 * The defaults can take a while — the first open of a company without a preset generates it — so
 * a prefill is requested when the form opens and applied when the answer is in, but only while
 * the user has not touched a remembered field: their own search always wins. It also waits for a
 * remember that is still on its way, so the form re-opened after "Add criteria" gets the search
 * just added rather than the one before it.
 */
export const useSearchDefaultsPrefill = ({
	getValues,
	reset,
	countryNameMap,
	onApplied,
}: UseSearchDefaultsPrefillOptions) => {
	const { isFetching } = useGetSearchDefaultsQuery();
	const { companyPreset, lastSearch, countryNames, loaded } =
		useAppSelector(searchDefaultsSelector);
	const [saveLastSearch, { isLoading: isRemembering }] = useSaveLastPersonSearchMutation();

	const [status, setStatus] = useState<SearchDefaultsStatus>("idle");
	// The remembered fields as they were when the prefill was requested; null when none is pending.
	const pendingFromRef = useRef<string | null>(null);
	const [pendingRequest, setPendingRequest] = useState(0);

	useEffect(() => {
		Object.entries(countryNames).forEach(([code, name]) => countryNameMap.current.set(code, name));
	}, [countryNames, countryNameMap]);

	const apply = (search: PersonLastSearch, nextStatus: SearchDefaultsStatus) => {
		const next = applySearchToFormValues(search, getValues());

		reset(next);
		onApplied(next);
		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- also reached from the effect, when the answer arrives
		setStatus(nextStatus);
	};

	/** A new source opens: prefill it once the defaults are in. */
	const requestPrefill = useCallback(() => {
		pendingFromRef.current = rememberedFields(getValues());
		setStatus("waiting");
		setPendingRequest((request) => request + 1);
	}, [getValues]);

	/** A saved source opens instead: nothing may be applied over it. */
	const cancelPrefill = useCallback(() => {
		pendingFromRef.current = null;
		setStatus("idle");
	}, []);

	useEffect(() => {
		const pendingFrom = pendingFromRef.current;

		if (pendingFrom === null || !loaded || isFetching || isRemembering) {
			return;
		}

		pendingFromRef.current = null;
		const search = lastSearch ?? companyPreset;

		if (!search || rememberedFields(getValues()) !== pendingFrom) {
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- the outcome of an answer that arrived later
			setStatus("idle");

			return;
		}

		apply(search, lastSearch ? "lastSearch" : "companyPreset");
	}, [pendingRequest, loaded, isFetching, isRemembering, lastSearch, companyPreset]);

	const resetToCompanyPreset = () => {
		if (companyPreset) {
			pendingFromRef.current = null;
			apply(companyPreset, "companyPreset");
		}
	};

	/**
	 * Remembers what the user just searched. Best effort: the search itself already succeeded,
	 * and a remember that fails only means the next form opens with the previous search.
	 */
	const rememberSearch = (values: FormValues) => {
		saveLastSearch(lastSearchFromFormValues(values))
			.unwrap()
			.catch((error: unknown) => console.warn("Could not remember the last search", error));
	};

	return {
		status,
		// Only while a request is running: a failed one leaves the form as it is, with no spinner.
		isLoadingDefaults: status === "waiting" && isFetching,
		canResetToCompanyPreset: companyPreset !== null,
		resetToCompanyPreset,
		requestPrefill,
		cancelPrefill,
		rememberSearch,
	};
};
